import { Button } from '@tempo-io/sui-button';
import { Warning } from '@tempo-io/sui-icons';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

export type ErrorAction = {
  label: string;
  action: () => void;
  variant?: 'primary' | 'secondary';
};

export type DefaultErrorProps = {
  title?: string;
  description?: string;
  icon?: ReactElement;
  actions?: ErrorAction[];
};

export const DefaultError = ({
  title,
  description,
  icon = <Warning className="text-text-error w-8 h-8" />,
  actions,
}: DefaultErrorProps) => {
  const { t } = useTranslation();

  const finalTitle = title ?? t('common.errors.global.title');
  const finalDescription = description ?? t('common.errors.global.description');

  const renderActions = () => {
    if (actions && actions.length > 0) {
      return (
        <div className="flex gap-2">
          {actions.map((action) => (
            <Button
              key={action.label}
              onClick={action.action}
              size="lg"
              variant={action.variant === 'secondary' ? 'outline' : 'default'}
            >
              {action.label}
            </Button>
          ))}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="flex w-full items-center justify-center px-4 bg-bg-surface-base min-h-screen">
      <div className="flex flex-col items-center gap-4 w-full max-w-md">
        <div className="bg-bg-error-subtle flex items-center justify-center rounded-full w-16 h-16">
          {icon}
        </div>

        <div className="flex flex-col items-center gap-2 text-center w-full">
          <h1 className="text-2xl font-semibold leading-8">{finalTitle}</h1>
          <p className="text-lg leading-7">{finalDescription}</p>
        </div>
        {renderActions()}
      </div>
    </div>
  );
};
